import { z } from 'zod'
import { decimalSchema, positiveMoneySchema, timestampSchema, uuidSchema } from './common.ts'
import { domainEventTypeSchema } from './domain-events.ts'
import {
  invoiceApprovalEntrySchema,
  invoiceApprovalStatusSchema,
  invoiceCommentSchema,
  invoiceLineItemSchema,
  invoiceSchema,
  invoiceStatusSchema,
} from './invoices.ts'
import { isoCurrencySchema } from './warehouse.ts'

export const paymentRequestStatusSchema = z.enum(['pending', 'approved', 'rejected', 'paid'])
export type PaymentRequestStatus = z.infer<typeof paymentRequestStatusSchema>
export const createPaymentInputSchema = z.object({
  amount: positiveMoneySchema,
  paidAt: timestampSchema.optional(),
  note: z.string().trim().max(1000).optional(),
})
const paymentRequestDueAtSchema = z.union([
  timestampSchema,
  z.iso.date().transform((value) => `${value}T00:00:00.000Z`),
])
const paymentRequestContentSchema = z.object({
  supplierId: uuidSchema,
  payerEntityId: uuidSchema,
  supplierInvoiceNumber: z.string().trim().min(1).max(100),
  supplierInvoiceDate: z.iso.date(),
  recipientIban: z
    .string()
    .trim()
    .toUpperCase()
    .transform((value) => value.replaceAll(' ', ''))
    .pipe(z.string().regex(/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/, 'Вкажіть коректний IBAN'))
    .nullable()
    .optional(),
  purpose: z.string().trim().min(1).max(1000),
  amount: positiveMoneySchema,
  dueAt: paymentRequestDueAtSchema.nullable().optional(),
  comment: z.string().trim().max(1000).nullable().optional(),
})
export const createPaymentRequestInputSchema = paymentRequestContentSchema
export type CreatePaymentRequestInput = z.infer<typeof createPaymentRequestInputSchema>
export const updatePaymentRequestInputSchema = paymentRequestContentSchema
  .partial()
  .strict()
  .refine((input) => Object.keys(input).length > 0, 'At least one field must be updated')
export type UpdatePaymentRequestInput = z.infer<typeof updatePaymentRequestInputSchema>
export const paymentRequestSchema = z.object({
  id: uuidSchema,
  supplierId: uuidSchema.nullable(),
  supplierName: z.string(),
  supplierEdrpou: z.string().nullable(),
  payerEntityId: uuidSchema.nullable(),
  payerEntityName: z.string().nullable(),
  payerEntityEdrpou: z.string().nullable(),
  supplierInvoiceNumber: z.string().nullable(),
  supplierInvoiceDate: z.iso.date().nullable(),
  recipientIban: z.string().nullable(),
  purpose: z.string(),
  comment: z.string().nullable(),
  amount: decimalSchema,
  dueAt: timestampSchema.nullable(),
  status: paymentRequestStatusSchema,
  requestedBy: z.string(),
  requestedRole: z.string(),
  reviewedBy: z.string().nullable(),
  reviewedAt: timestampSchema.nullable(),
  paidBy: z.string().nullable(),
  paidAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
})
export type PaymentRequest = z.infer<typeof paymentRequestSchema>
export const updatePaymentRequestStatusInputSchema = z.object({
  status: z.enum(['approved', 'rejected', 'paid']),
  comment: z.string().trim().min(1).max(1000).optional(),
})
/** A reversal is a compensating row that stays in the ledger forever, so it must say why. */
export const reversePaymentInputSchema = z.object({ reason: z.string().trim().min(1).max(1000) })

/**
 * One line of a payment request's audit trail: either a status transition (from the append-only
 * status history) or an edit of its details (from the domain event outbox).
 */
export const paymentRequestHistoryEntrySchema = z.object({
  id: uuidSchema,
  kind: z.enum(['status', 'details', 'attachment_added', 'attachment_removed']),
  fromStatus: paymentRequestStatusSchema.nullable(),
  toStatus: paymentRequestStatusSchema.nullable(),
  changedFields: z.array(z.string()),
  fileName: z.string().nullable(),
  comment: z.string().nullable(),
  changedBy: z.string().nullable(),
  changedByName: z.string().nullable(),
  changedAt: timestampSchema,
})
export type PaymentRequestHistoryEntry = z.infer<typeof paymentRequestHistoryEntrySchema>
export const accountingInvoiceSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  dealId: uuidSchema.nullable(),
  clientName: z.string(),
  amount: decimalSchema,
  currencyCode: isoCurrencySchema,
  uahEquivalent: decimalSchema,
  paid: decimalSchema,
  remaining: decimalSchema,
  effectiveStatus: z.enum(['draft', 'issued', 'awaiting', 'paid', 'overdue']),
  approvalStatus: invoiceApprovalStatusSchema,
  dueAt: timestampSchema.nullable(),
  issuedAt: timestampSchema.nullable(),
})
export type AccountingInvoice = z.infer<typeof accountingInvoiceSchema>

export const accountingInvoicePaymentSchema = z.object({
  id: uuidSchema,
  invoiceId: uuidSchema,
  amount: decimalSchema,
  paidAt: timestampSchema,
  reversedPaymentId: uuidSchema.nullable(),
  reversalReason: z.string().nullable(),
  note: z.string().nullable(),
  createdBy: z.string(),
  createdByName: z.string().nullable(),
  createdAt: timestampSchema,
})
export type AccountingInvoicePayment = z.infer<typeof accountingInvoicePaymentSchema>
export const accountingInvoiceEventSchema = z.object({
  id: uuidSchema,
  type: domainEventTypeSchema,
  payload: z.record(z.string(), z.unknown()),
  actorName: z.string().nullable(),
  occurredAt: timestampSchema,
})
export type AccountingInvoiceEvent = z.infer<typeof accountingInvoiceEventSchema>
export const accountingInvoiceDetailSchema = z.object({
  invoice: invoiceSchema,
  clientName: z.string(),
  creatorName: z.string().nullable(),
  dealName: z.string().nullable(),
  orderNumber: z.string().nullable(),
  paid: decimalSchema,
  remaining: decimalSchema,
  effectiveStatus: invoiceStatusSchema,
  lineItems: z.array(invoiceLineItemSchema),
  payments: z.array(accountingInvoicePaymentSchema),
  comments: z.array(invoiceCommentSchema),
  approvals: z.array(invoiceApprovalEntrySchema),
  events: z.array(accountingInvoiceEventSchema),
})
export type AccountingInvoiceDetail = z.infer<typeof accountingInvoiceDetailSchema>
